import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Link, Skeleton, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { describeCreditTerm, formatFecha } from '../../helpers/describe';
import { vencimientoAutomaticoTooltip } from '../../constants';
import { IResolveDueDateData } from '../../types';

interface DueDateRuleNoticeProps {
	loading: boolean;
	resolved: IResolveDueDateData | null;
	/** El valor que tiene hoy el campo de vencimiento (YYYY-MM-DD). */
	currentDueDate: string;
	/** Restaura el vencimiento sugerido por la condición. */
	onRestore: () => void;
	/** Link a la ficha del cliente (vista cliente). */
	passengerHref?: string;
}

/**
 * Explica de dónde salió la fecha de vencimiento en el drawer de facturación.
 *
 * Tres criterios de diseño:
 *  1. Nada mágico: si el sistema completó la fecha, se dice cuál condición la
 *     generó y se puede ir a verla en la ficha del cliente.
 *  2. Divergencia visible: si el usuario cambió la fecha a mano, no se oculta —
 *     se avisa qué decía la condición y se ofrece volver en un click.
 *  3. Nunca inventar: sin condición, o con una que requiere revisión manual, se
 *     dice explícitamente que la fecha va a mano.
 */
export function DueDateRuleNotice({
	loading,
	resolved,
	currentDueDate,
	onRestore,
	passengerHref,
}: DueDateRuleNoticeProps): JSX.Element | null {
	if (loading) return <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />;
	if (!resolved) return null;

	const verCondicion = passengerHref ? (
		<Link
			component={RouterLink}
			to={passengerHref}
			target="_blank"
			rel="noopener"
			underline="hover"
			sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, whiteSpace: 'nowrap' }}
		>
			Ver condición del cliente
			<OpenInNewIcon sx={{ fontSize: 13 }} />
		</Link>
	) : null;

	// ── Cliente sin condición ────────────────────────────────────────────────
	if (resolved.sinCondicion) {
		return (
			<Alert severity="warning" sx={{ py: 0.75 }}>
				Este cliente <strong>no tiene condición de crédito asignada</strong>: completá el vencimiento a mano.{' '}
				{verCondicion}
			</Alert>
		);
	}

	const { term, resultado } = resolved;
	if (!term || !resultado) return null;

	// ── Condición que no puede calcularse sola ───────────────────────────────
	if (resultado.requiereRevisionManual) {
		return (
			<Alert severity="warning" sx={{ py: 0.75 }}>
				<AlertTitle sx={{ fontSize: 13, mb: 0.25 }}>
					La condición <strong>{term.name}</strong> requiere definir el vencimiento a mano
				</AlertTitle>
				<Typography variant="caption">
					{resultado.alertas[0] ?? 'Es una condición personalizada.'} {verCondicion}
				</Typography>
			</Alert>
		);
	}

	const sugerida = resultado.fechaVencFinal;
	const divergente = Boolean(sugerida && currentDueDate && currentDueDate !== sugerida);

	// ── El usuario cambió la fecha sugerida ──────────────────────────────────
	if (divergente) {
		return (
			<Alert
				severity="info"
				icon={false}
				sx={{ py: 0.75, bgcolor: 'warning.50', borderColor: 'warning.200', border: 1 }}
				action={
					<Button
						size="small"
						startIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
						onClick={onRestore}
						sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
					>
						Restaurar
					</Button>
				}
			>
				<Typography variant="body2">
					Modificaste el vencimiento sugerido. Según la condición <strong>{term.name}</strong> del cliente, vencía el{' '}
					<strong>{formatFecha(sugerida)}</strong>.
				</Typography>
			</Alert>
		);
	}

	// ── Caso feliz: se completó solo ─────────────────────────────────────────
	return (
		<Alert severity="success" icon={<AutoAwesomeIcon fontSize="inherit" />} sx={{ py: 0.75 }}>
			<Stack spacing={0.25}>
				<Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
					<Typography variant="body2">
						Vencimiento completado automáticamente según la condición{' '}
						<strong>
							#{term.id} “{term.name}”
						</strong>{' '}
						del cliente.
					</Typography>
					<SectionInfoTooltip content={vencimientoAutomaticoTooltip} />
				</Box>
				<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
					<Typography variant="caption" color="text.secondary">
						{describeCreditTerm(term)}
						{resultado.diasPlazoEfectivo != null && ` · ${resultado.diasPlazoEfectivo} días de plazo`}
					</Typography>
					{verCondicion}
				</Box>

				{/* Modificadores que efectivamente cambiaron la fecha: se avisan,
				    porque si no el usuario no entiende por qué no es la fecha "redonda". */}
				{resultado.aplicoAjusteFinDeSemana && (
					<Typography variant="caption" color="text.secondary">
						Caía fin de semana: se movió al día hábil más cercano.
					</Typography>
				)}
				{resultado.aplicoTopeViaje && (
					<Typography variant="caption" color="text.secondary">
						Se adelantó por proximidad del viaje.
					</Typography>
				)}
				{resultado.alertas.map((a) => (
					<Typography key={a} variant="caption" color="warning.main">
						⚠️ {a}
					</Typography>
				))}
			</Stack>
		</Alert>
	);
}
